import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmspStation } from '../../../interface/rfid-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { createCardPoolApi } from '../../../redux/slices/CardGroupSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import MultiSelectDropdown, { Option } from '../../../shared-components/multi-select-field/MultiSelectField';
import rfidApi from '../../../utils/api/rfid';

interface AddGroupModalProperties {
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Resolves the full tenant network station ID (e.g. IN:ENR:1bfd0119-...)
 * @param {EmspStation} station - Station object
 * @returns {string} Fully qualified station ID
 */
const getFullStationId = (station: EmspStation): string => {
  const baseId = station.stationId || station.id || station._id || '';
  if (!baseId) return '';
  if (baseId.includes(':')) return baseId;
  const prefix = [station.countryCode, station.partyId].filter(Boolean).join(':');
  return prefix ? `${prefix}:${baseId}` : baseId;
};

/**
 * Modal form component to create a new Card Group
 * @param {AddGroupModalProperties} properties - Component properties
 * @returns {React.JSX.Element} Add card group form modal content
 */
function AddGroupModal(properties: AddGroupModalProperties): React.JSX.Element {
  const { onClose, onSuccess } = properties;
  const { t, i18n } = useTranslation(['devices', 'sharedComponent']);
  const dispatch = useAppDispatch();
  const { actionLoading } = useAppSelector((state) => state.cardGroups);

  const [name, setName] = useState('');
  const [stationList, setStationList] = useState<EmspStation[]>([]);
  const [stationOptions, setStationOptions] = useState<Option[]>([]);
  const [selectedStationIds, setSelectedStationIds] = useState<string[]>([]);
  const [stationsLoading, setStationsLoading] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    selectedStationIds?: string;
  }>({});

  useEffect(() => {
    let isMounted = true;
    setStationsLoading(true);

    /**
     * Fetches authorized station options from EMSP API
     * @returns {Promise<void>} Resolves when stations are loaded
     */
    const loadStationOptions = async (): Promise<void> => {
      try {
        const response = await rfidApi.fetchStationsList();
        if (isMounted) {
          const rawResults =
            response?.data?.results ||
            response?.data?.data ||
            (Array.isArray(response?.data) ? response?.data : []);
          const list: EmspStation[] = Array.isArray(rawResults) ? rawResults : [];
          setStationList(list);
          const options: Option[] = list.map((item: EmspStation) => {
            const fullId = getFullStationId(item);
            const party = [item.countryCode, item.partyId].filter(Boolean).join(':');
            const displayName = item.name || fullId;
            const labelText = item.name
              ? `${item.name}${party ? ` (${party})` : ''} - ${fullId}`
              : `${fullId}`;

            return {
              ...item,
              label: labelText,
              value: fullId,
              labelNode: (
                <div className="rfid-customer-select-item">
                  <span className="rfid-customer-select-item__name">{displayName}</span>
                  <span className="rfid-customer-select-item__id">{fullId}</span>
                  {party && <span className="rfid-customer-select-item__contact">{party}</span>}
                </div>
              ),
            };
          });
          setStationOptions(options);
          setSelectedStationIds((previous) =>
            previous.filter((id) => options.some((opt) => opt.value === id)),
          );
        }
      } catch {
        // Fallback handled gracefully
      } finally {
        if (isMounted) {
          setStationsLoading(false);
        }
      }
    };

    loadStationOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * Handles form submit to create card group with validation
   * @returns {Promise<void>} Resolves when card group creation completes
   */
  const handleSubmit = async (): Promise<void> => {
    const newErrors: {
      name?: string;
      selectedStationIds?: string;
    } = {};

    if (!name.trim()) {
      newErrors.name = t('nameRequired', {
        defaultValue: 'Group name is required',
      });
    }

    if (!selectedStationIds || selectedStationIds.length === 0) {
      newErrors.selectedStationIds = t('stationRequired', {
        defaultValue: 'Please select at least one charging station',
      });
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstErrorMessage = Object.values(newErrors)[0];
      if (firstErrorMessage) {
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'error',
            code: 400,
            message: firstErrorMessage,
          }),
        );
      }
      return;
    }

    try {
      await dispatch(
        createCardPoolApi({
          kind: 'GROUP',
          name: name.trim(),
          stationIds: selectedStationIds,
        }),
      ).unwrap();

      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch {
      // Error notification toast is dispatched automatically by createCardPoolApi
    }
  };

  /**
   * Handles cancelling card group creation, resetting form state and closing panel
   * @returns {void}
   */
  const handleCancel = (): void => {
    setName('');
    setSelectedStationIds([]);
    setErrors({});
    onClose();
  };

  const selectedStations = stationList.filter((item) =>
    selectedStationIds.includes(getFullStationId(item)),
  );

  return (
    <div className="rfid-modal-form" dir={i18n.dir()}>
      <div className="rfid-modal-form__body">
        <div className="rfid-modal-form__field">
          <CustomInput
            inputLabel={t('groupName', { defaultValue: 'Group Name' })}
            type="text"
            name="name"
            placeholder={t('corporateFleetPlaceholder')}
            value={name}
            valueRequired
            error={Boolean(errors.name)}
            errorMessage={errors.name}
            onChangeValue={(_, value) => {
              setName(String(value));
              if (errors.name) {
                setErrors((previous) => ({ ...previous, name: undefined }));
              }
            }}
          />
        </div>

        <div className="rfid-modal-form__field">
          <MultiSelectDropdown
            title={t('chargingStations', { defaultValue: 'Charging Stations' })}
            name="selectedStationIds"
            options={stationOptions}
            selectedValues={selectedStationIds}
            drillDownLevels={['countryCode', 'partyId']}
            valueRequired
            error={Boolean(errors.selectedStationIds)}
            errorMessage={errors.selectedStationIds}
            onSelectionChange={(_, values) => {
              setSelectedStationIds(values);
              if (errors.selectedStationIds) {
                setErrors((previous) => ({ ...previous, selectedStationIds: undefined }));
              }
            }}
            placeholder={
              stationsLoading
                ? t('loadingStations', { defaultValue: 'Loading charging stations...' })
                : t('selectStationsPlaceholder', {
                    defaultValue: 'Select one or more charging stations',
                  })
            }
            placeholderFilled={
              selectedStationIds.length > 1
                ? `${selectedStationIds.length} ${t('stations', { defaultValue: 'stations' })}`
                : `1 ${t('station', { defaultValue: 'station' })}`
            }
            search
          />
          {errors.selectedStationIds && (
            <span className="text-danger">{errors.selectedStationIds}</span>
          )}

          {selectedStations.length > 0 && (
            <div className="rfid-selected-stations-list">
              <div className="rfid-selected-stations-list__header">
                <span className="rfid-selected-stations-list__title">
                  {t('selectedStationsCount', {
                    defaultValue: 'Selected Stations',
                  })}{' '}
                  ({selectedStations.length})
                </span>
                {selectedStations.length > 1 && (
                  <button
                    type="button"
                    className="rfid-selected-stations-list__clear-btn"
                    onClick={() => setSelectedStationIds([])}>
                    {t('clearAll', { defaultValue: 'Clear all' })}
                  </button>
                )}
              </div>
              <div className="rfid-selected-stations-list__items">
                {selectedStations.map((station) => {
                  const fullId = getFullStationId(station);
                  const party = [station.countryCode, station.partyId].filter(Boolean).join(':');
                  return (
                    <div key={fullId} className="rfid-selected-user-card">
                      <div className="rfid-selected-user-card__header">
                        <span className="rfid-selected-user-card__name">
                          {station.name || fullId}
                        </span>
                        <div className="rfid-selected-user-card__header-right">
                          <span className="rfid-selected-user-card__id">{fullId}</span>
                          <button
                            type="button"
                            className="rfid-selected-user-card__remove-btn"
                            onClick={() =>
                              setSelectedStationIds((previous) =>
                                previous.filter((id) => id !== fullId),
                              )
                            }
                            aria-label={t('removeAriaLabel', { defaultValue: 'Remove {{name}}', name: station.name || fullId })}
                            title={t('remove', { defaultValue: 'Remove' })}>
                            ✕
                          </button>
                        </div>
                      </div>
                      {party && (
                        <div className="rfid-selected-user-card__details">
                          <div className="rfid-selected-user-card__detail-item">
                            <span className="rfid-selected-user-card__detail-label">
                              {t('operator', { defaultValue: 'Party / Operator' })}:
                            </span>
                            <span className="rfid-selected-user-card__detail-value">{party}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="rfid-modal-form__actions">
        <MyButton
          label={t('cancel', { defaultValue: 'Cancel' })}
          buttonType="outline"
          onClickFunc={handleCancel}
        />
        <MyButton
          label={
            actionLoading
              ? t('creatingGroup', { defaultValue: 'Creating Group...' })
              : t('createGroup', { defaultValue: 'Create Card Group' })
          }
          buttonType="primary"
          onClickFunc={handleSubmit}
          disableButton={actionLoading}
        />
      </div>
    </div>
  );
}

export default AddGroupModal;
